import { useCallback, useEffect, useState } from 'react';

import { SelectOptionType } from '@/shared/config';

import { DEFAULT_TRANSCRIPTOR_LANGUAGE } from '../config/constants';
import {
  TranscriptorPreferences,
  UseTranscriptorPreferencesReturn,
} from '../config/types';
import { getStoredPreferences, savePreferencesToStorage } from '../lib';

export const useTranscriptorPreferences = (
  languageOptions: SelectOptionType[],
): UseTranscriptorPreferencesReturn => {
  const storedPreferences = getStoredPreferences();
  const isLanguageAvailable = languageOptions.some(
    (option) => option.value === storedPreferences.language,
  );

  const validatedLanguage = isLanguageAvailable
    ? storedPreferences.language
    : DEFAULT_TRANSCRIPTOR_LANGUAGE;

  const initialPreferences: TranscriptorPreferences = {
    ...storedPreferences,
    language: validatedLanguage,
  };

  const [preferences, setPreferences] =
    useState<TranscriptorPreferences>(initialPreferences);

  useEffect(() => {
    if (languageOptions.length === 0) {
      return;
    }

    setPreferences((current) => {
      const isAvailable = languageOptions.some(
        (option) => option.value === current.language,
      );

      if (isAvailable) {
        return current;
      }

      return { ...current, language: DEFAULT_TRANSCRIPTOR_LANGUAGE };
    });
  }, [languageOptions]);

  const updatePreferences = useCallback(
    (updates: Partial<TranscriptorPreferences>) => {
      setPreferences((current) => {
        const newPreferences = { ...current, ...updates };
        savePreferencesToStorage(newPreferences);
        return newPreferences;
      });
    },
    [],
  );

  const resetPreferences = useCallback(() => {
    const currentStoredPreferences = getStoredPreferences();
    const isAvailable = languageOptions.some(
      (option) => option.value === currentStoredPreferences.language,
    );

    setPreferences({
      ...currentStoredPreferences,
      language: isAvailable
        ? currentStoredPreferences.language
        : DEFAULT_TRANSCRIPTOR_LANGUAGE,
    });
  }, [languageOptions]);

  return {
    preferences,
    updatePreferences,
    resetPreferences,
  };
};
